/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

// components imports
import { ColumnsType } from "antd/es/table";
import { Col, Grid, Row, Table, Tooltip } from "antd";
import { CalendarOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";
import { PaymentStatus } from "@/components/PaymentStatus";
import { PaymentCardBrand } from "@/components/PaymentCardBrand";

// misc imports
import { useGetPayments } from "@/api/user";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { useUserInfo } from "@/api/user";
import { IPaymentMethod, PaymentIntentStatus } from "@/interfaces/PaymentIntent.interface";
import { numberToFixed } from "@/utils/number/numberToFixed";
import { CARD_BRANDS, CURRENCY_SYMBOLS, PAGINATION_OPTIONS } from "@/constants";
import { ICurrency } from "@/interfaces/SubscriptionPlans";
import { Breakpoint } from "antd/lib";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

const { useBreakpoint } = Grid;

interface IPaymentDataTable {
  amount: number;
  currency: ICurrency;
  status: PaymentIntentStatus;
  payment_method: IPaymentMethod;
  date: string;
  description: string;
}

export const Payments = (): JSX.Element => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const tokensInstance = Tokens;
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const [localPaymentsData, setLocalPaymentsData] = useState<IPaymentDataTable[] | undefined>(undefined);
  const tokenSub = tokensInstance.getTokenSub(accessToken) as string;
  const { data: paymentsData, isLoading: isLoadingPaymentsData } = useGetPayments(tokenSub);
  const { data: userInfo } = useUserInfo(tokenSub);
  const isScreenBelow = (size: "sm" | "md" | "lg" | "xl" | "xxl") => screens.xs || (screens.sm && !screens[size]);

  // USE EFFECT
  useEffect(() => {
    if (!paymentsData) {
      return;
    }

    setLocalPaymentsData(
      paymentsData.map((payment) => {
        const _brand = payment.card_brand;
        const _last4 = payment.card_last_4;
        return {
          amount: payment.amount,
          status: payment.status,
          currency: payment.currency,
          payment_method: {
            brand: _brand,
            last4: _last4,
          },
          date: payment.creation_date,
          description: payment.description,
        };
      })
    );
  }, [paymentsData]);

  // Columns configuration for the Payments table
  const columns: ColumnsType<IPaymentDataTable> = [
    {
      title: t("subscriptions_screen.payments.table.amount"),
      dataIndex: "amount",
      key: "amount",
      render: (amount: number, record: IPaymentDataTable) => (
        <span>
          {parse(CURRENCY_SYMBOLS[record.currency].icon)}
          {numberToFixed(amount)}
        </span>
      ),
    },
    {
      title: t("subscriptions_screen.payments.table.payment_method"),
      dataIndex: "payment_method",
      key: "payment_method",
      responsive: ["md"],
      render: (data: IPaymentMethod) => {
        const last4 = data.last4 ? "&#x2022;".repeat(4) + data.last4 : "";
        const brand = data.brand;
        return (
          <>
            <Tooltip title={brand ? CARD_BRANDS[brand].brand : ''}>
              <span>
                <PaymentCardBrand brand={brand} />
              </span>
            </Tooltip>
            {parse(last4)}
          </>
        );
      },
    },
    {
      title: t("subscriptions_screen.payments.table.status"),
      dataIndex: "status",
      key: "status",
      responsive: ["md"],
      render: (status: PaymentIntentStatus) => <PaymentStatus status={status} responsive />,
    },
    {
      title: t("subscriptions_screen.payments.table.date"),
      dataIndex: "date",
      key: "date",
      responsive: ["md"],
      render: (date: string) => {
        const formatted = getFormattedDate(date, userInfo?.timezone, "DD-MM-YYYY");
        return isScreenBelow("md") ? (
          <Tooltip title={formatted}>
            <CalendarOutlined />
          </Tooltip>
        ) : (
          <>{formatted}</>
        );
      },
    },
    {
      title: t("subscriptions_screen.payments.table.description"),
      dataIndex: "description",
      key: "description",
      responsive: ['md'] as Breakpoint[]
    },
  ];

  const hasInfoColumn = () => columns.find((col) => col.key === "information");

  if (isScreenBelow("md") && !hasInfoColumn()) {
    columns.push({
      title: t("subscriptions_screen.payments.table.info"),
      key: "information",
      responsive: ["xs", "sm", "md"],
      render: (record: IPaymentDataTable) => {
        const formatted = getFormattedDate(record.date.toString(), userInfo?.timezone, "DD-MM-YYYY");
        return (
          <>
            <span style={{ marginRight: "15px" }}>
              <PaymentStatus status={record.status} responsive />
            </span>
            <span style={{ marginRight: "15px" }}>
              <Tooltip title={formatted}>
                <CalendarOutlined />
              </Tooltip>
            </span>

            <Tooltip title={record.description}>
              <ExclamationCircleOutlined />
            </Tooltip>
          </>
        );
      },
    });
  }

  if (!localPaymentsData && isLoadingPaymentsData) {
    return <DataLoadingSkeleton />;
  }

  return (
    <Row>
      <Col xs={24}>
        <Table size="small" bordered rowKey="date" columns={columns} dataSource={localPaymentsData} pagination={{ ...PAGINATION_OPTIONS }} />
      </Col>
    </Row>
  );
};
